import type { Editor, ChainedCommands } from "@tiptap/react";
import {
  BetweenHorizontalEnd,
  BetweenHorizontalStart,
  BetweenVerticalEnd,
  BetweenVerticalStart,
  Columns3,
  Rows3,
  Table,
  TableCellsMerge,
  TableCellsSplit,
  Trash,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ComponentType } from "react";
import { ToolbarButton } from "./ToolbarButton";

interface TableAction {
  label: string;
  icon: ComponentType<{ className?: string }>;
  run: (chain: ChainedCommands) => ChainedCommands;
  destructive?: boolean;
}

const ROW_COL_ACTIONS: TableAction[] = [
  { label: "Üste satır ekle", icon: BetweenHorizontalStart, run: (c) => c.addRowBefore() },
  { label: "Alta satır ekle", icon: BetweenHorizontalEnd, run: (c) => c.addRowAfter() },
  { label: "Satırı sil", icon: Rows3, run: (c) => c.deleteRow(), destructive: true },
  { label: "Sola sütun ekle", icon: BetweenVerticalStart, run: (c) => c.addColumnBefore() },
  { label: "Sağa sütun ekle", icon: BetweenVerticalEnd, run: (c) => c.addColumnAfter() },
  { label: "Sütunu sil", icon: Columns3, run: (c) => c.deleteColumn(), destructive: true },
];

const CELL_ACTIONS: TableAction[] = [
  { label: "Başlık satırı aç/kapat", icon: Rows3, run: (c) => c.toggleHeaderRow() },
  { label: "Hücreleri birleştir", icon: TableCellsMerge, run: (c) => c.mergeCells() },
  { label: "Hücreyi böl", icon: TableCellsSplit, run: (c) => c.splitCell() },
];

// Tablo ekleme ve düzenleme menüsü. İmleç tablo dışındayken yalnızca ekleme sunulur.
export function TableMenu({ editor, inTable }: { editor: Editor; inTable: boolean }) {
  const item = (a: TableAction) => (
    <DropdownMenuItem
      key={a.label}
      onSelect={() => a.run(editor.chain().focus()).run()}
      className={a.destructive ? "text-destructive focus:text-destructive" : undefined}
    >
      <a.icon className="size-4" />
      {a.label}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <ToolbarButton label="Tablo" active={inTable}>
          <Table />
        </ToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuItem
          onSelect={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          <Table className="size-4" />
          Tablo ekle (3×3)
        </DropdownMenuItem>
        {inTable && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Satır / sütun</DropdownMenuLabel>
            {ROW_COL_ACTIONS.map(item)}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Hücre</DropdownMenuLabel>
            {CELL_ACTIONS.map(item)}
            <DropdownMenuSeparator />
            {item({
              label: "Tabloyu sil",
              icon: Trash,
              run: (c) => c.deleteTable(),
              destructive: true,
            })}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
